import React, { useState, useRef, useId, useEffect } from 'react';
import ReactDOM from 'react-dom';
import { useTranslation } from 'react-i18next';
import clsx from 'clsx';
import { usePopper } from 'react-popper';
import { motion, AnimatePresence } from 'framer-motion';

const inputHoverFocusStyles =
  'hover:shadow-[2px_2px_0px_0px_rgba(0,0,0,0.9)] hover:-translate-x-px hover:-translate-y-px focus:shadow-[2px_2px_0px_0px_rgba(0,0,0,0.9)] focus:-translate-x-px focus:-translate-y-px';

export interface ColorPickerFieldProps {
  label?: string;
  labelKey?: string;
  value: string;
  onChange: (color: string) => void;
  descriptionText?: string;
  descriptionTextKey?: string;
  errorText?: string;
  errorTextKey?: string;
  disabled?: boolean;
  size?: 'sm' | 'md' | 'lg';
  paletteColors?: string[];
  showTextInputInPopover?: boolean;
  id?: string;
  name?: string;
  containerClassName?: string;
  labelClassName?: string;
  swatchClassName?: string;
  popoverClassName?: string;
  inputClassName?: string;
  portal?: boolean;
  allowClear?: boolean;
  onOpenChange?: (open: boolean) => void;
}

export const ColorPickerField: React.FC<ColorPickerFieldProps> = ({
  label,
  labelKey,
  value,
  onChange,
  descriptionText,
  descriptionTextKey,
  errorText,
  errorTextKey,
  disabled = false,
  size = 'md',
  paletteColors = [
    '#FF0000', '#00FF00', '#0000FF', '#FFFF00',
    '#FF00FF', '#00FFFF', '#000000', '#FFFFFF',
    '#1e293b', '#3b82f6', '#10b981', '#f59e0b'
  ],
  showTextInputInPopover = true,
  id,
  name,
  containerClassName,
  labelClassName,
  swatchClassName,
  popoverClassName,
  inputClassName,
  portal = true,
  allowClear = false,
  onOpenChange,
}) => {
  const { t } = useTranslation();
  const autoId = useId();
  const fieldId = id || autoId;
  const fieldName = name || fieldId;

  const [isOpen, setIsOpen] = useState(false);
  const [inputValue, setInputValue] = useState(value);
  const [customColor, setCustomColor] = useState(guessHex(value));

  const referenceElement = useRef<HTMLButtonElement>(null);
  const popperElement = useRef<HTMLDivElement>(null);

  const { styles, attributes, update } = usePopper(
    referenceElement.current,
    popperElement.current,
    {
      placement: 'bottom-start',
      strategy: portal ? 'fixed' : 'absolute',
      modifiers: [
        { name: 'offset', options: { offset: [0, 8] } },
        { name: 'preventOverflow', options: { padding: 8, rootBoundary: 'viewport' } },
        { name: 'flip', options: { padding: 8, rootBoundary: 'viewport' } },
        { name: 'computeStyles', options: { gpuAcceleration: false } },
      ],
    }
  );

  const [fallbackStyle, setFallbackStyle] = useState<React.CSSProperties | null>(null);

  useEffect(() => {
    setInputValue(value);
    setCustomColor(guessHex(value));
  }, [value]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        isOpen &&
        referenceElement.current &&
        !referenceElement.current.contains(event.target as Node) &&
        popperElement.current &&
        !popperElement.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
        onOpenChange?.(false);
      }
    };
    const handleEscape = (event: KeyboardEvent) => {
      if (isOpen && event.key === 'Escape') {
        setIsOpen(false);
        onOpenChange?.(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleEscape);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscape);
    };
  }, [isOpen, onOpenChange]);

  useEffect(() => {
    if (!isOpen) {
      setFallbackStyle(null);
      return;
    }
    if (referenceElement.current) {
      const rect = referenceElement.current.getBoundingClientRect();
      setFallbackStyle({
        position: portal ? 'fixed' : 'absolute',
        top: rect.bottom + 8,
        left: rect.left,
        zIndex: 1001,
      });
    } else {
      setFallbackStyle(null);
    }
    const raf = requestAnimationFrame(() => {
      update?.();
    });
    return () => cancelAnimationFrame(raf);
  }, [isOpen, portal, update]);

  useEffect(() => {
    if (!isOpen) return;
    const onScrollOrResize = () => update?.();
    window.addEventListener('scroll', onScrollOrResize, true);
    window.addEventListener('resize', onScrollOrResize, true);
    return () => {
      window.removeEventListener('scroll', onScrollOrResize, true);
      window.removeEventListener('resize', onScrollOrResize, true);
    };
  }, [isOpen, update]);

  const resolvedLabel = labelKey ? t(labelKey) : label;
  const resolvedDescriptionText = descriptionTextKey ? t(descriptionTextKey) : descriptionText;
  const resolvedErrorText = errorTextKey ? t(errorTextKey) : errorText;
  const hasError = !!resolvedErrorText;

  const handleSwatchClick = () => {
    if (disabled) return;
    const next = !isOpen;
    setIsOpen(next);
    onOpenChange?.(next);
  };

  const handleColorSelect = (color: string) => {
    onChange(color);
    setInputValue(color);
    setCustomColor(guessHex(color));
  };

  const handleTextInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setInputValue(e.target.value);
  };

  const isValidColorText = (col: string) =>
    /^#([0-9A-Fa-f]{3}|[0-9A-Fa-f]{6})$/i.test(col) ||
    /^(rgb|rgba)\(\s*\d+\s*,\s*\d+\s*,\s*\d+(?:\s*,\s*(0|1|0?\.\d+))?\s*\)$/i.test(col);

  const handleTextInputBlur = () => {
    if (isValidColorText(inputValue)) {
      onChange(inputValue);
      setCustomColor(guessHex(inputValue));
    } else {
      setInputValue(value);
    }
  };

  const handleCustomInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    const hex = e.target.value;
    setCustomColor(hex);
    if (isValidHex(hex)) {
      onChange(hex);
      setInputValue(hex);
    }
  };

  const handleClear = () => {
    if (!allowClear) return;
    onChange('');
    setInputValue('');
    setCustomColor('#000000');
  };

  const sizeClasses = {
    sm: { swatch: 'w-8 h-8' },
    md: { swatch: 'w-10 h-10' },
    lg: { swatch: 'w-12 h-12' },
  };

  const swatchBaseClasses = clsx(
    'relative rounded-lg border-2 border-black flex items-center justify-center',
    'transition-all duration-150 ease-out transform',
    !disabled && 'cursor-pointer hover:-translate-x-0.5 hover:-translate-y-0.5 hover:shadow-[2px_2px_0_0_black]',
    !disabled && 'active:translate-x-0 active:translate-y-0 active:shadow-[1px_1px_0_0_black]',
    '[.theme-liquid-glass_&]:border [.theme-liquid-glass_&]:border-white/30',
    '[.theme-liquid-glass_&]:rounded-xl [.theme-liquid-glass_&]:backdrop-blur-sm',
    !disabled && '[.theme-liquid-glass_&]:hover:translate-x-0 [.theme-liquid-glass_&]:hover:translate-y-0 [.theme-liquid-glass_&]:hover:shadow-none',
    disabled && 'opacity-60 cursor-not-allowed',
    sizeClasses[size].swatch,
    swatchClassName
  );

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: { opacity: 1, transition: { duration: 0.2, when: 'beforeChildren' } },
  };
  const panelVariants = {
    hidden: { y: -10, opacity: 0 },
    visible: { y: 0, opacity: 1, transition: { duration: 0.2, ease: 'easeOut' } },
  };

  const PopoverBody = (
    <motion.div
      ref={popperElement}
      style={{ ...(fallbackStyle || {}), ...(styles.popper || {}) }}
      {...(attributes.popper || {})}
      className="z-[1001]"
      variants={containerVariants}
      initial="hidden"
      animate="visible"
      exit="hidden"
    >
      <motion.div
        className={clsx(
          'bg-white border-2 border-black rounded-xl shadow-lg p-4 w-72',
          '[.theme-liquid-glass_&]:bg-slate-800/90 [.theme-liquid-glass_&]:border-white/20',
          '[.theme-liquid-glass_&]:rounded-2xl [.theme-liquid-glass_&]:backdrop-blur-xl',
          '[.theme-liquid-glass_&]:shadow-[0_24px_64px_rgba(0,0,0,0.4)]',
          popoverClassName
        )}
        variants={panelVariants}
      >
        {showTextInputInPopover && (
          <div className="mb-3">
            <label className="block text-xs font-semibold text-slate-700 mb-1 [.theme-liquid-glass_&]:text-white/80">
              {t('Color (hex or rgb/rgba)')}
            </label>
            <input
              type="text"
              value={inputValue}
              onChange={handleTextInputChange}
              onBlur={handleTextInputBlur}
              className={clsx(
                'w-full px-3 py-2 text-base rounded-lg border-2 border-black bg-white',
                '[.theme-liquid-glass_&]:border [.theme-liquid-glass_&]:border-white/20',
                '[.theme-liquid-glass_&]:bg-white/5 [.theme-liquid-glass_&]:text-white',
                '[.theme-liquid-glass_&]:placeholder:text-white/40 [.theme-liquid-glass_&]:rounded-xl',
                'focus:outline-none',
                !disabled && inputHoverFocusStyles,
                !disabled && '[.theme-liquid-glass_&]:hover:translate-x-0 [.theme-liquid-glass_&]:hover:translate-y-0 [.theme-liquid-glass_&]:hover:shadow-none',
                !disabled && '[.theme-liquid-glass_&]:focus:translate-x-0 [.theme-liquid-glass_&]:focus:translate-y-0 [.theme-liquid-glass_&]:focus:shadow-[0_0_0_2px_rgba(139,92,246,0.3)]',
                inputClassName
              )}
              placeholder="#RRGGBB or rgb(a)"
              disabled={disabled}
            />
          </div>
        )}

        <div className="mb-3">
          <label className="block text-xs font-semibold text-slate-700 mb-1 [.theme-liquid-glass_&]:text-white/80">
            {t('Custom color')}
          </label>
          <div className="flex items-center gap-3">
            <input
              type="color"
              value={isValidHex(customColor) ? customColor : '#000000'}
              onChange={handleCustomInput}
              className="w-10 h-10 rounded border border-slate-300 cursor-pointer [.theme-liquid-glass_&]:border-white/30"
              disabled={disabled}
              aria-label={t('Pick custom color')}
            />
            <div className="text-xs text-slate-600 [.theme-liquid-glass_&]:text-white/70">
              {t('Selected')}: <span className="font-mono">{customColor}</span>
            </div>
          </div>
        </div>

        {paletteColors && paletteColors.length > 0 && (
          <div className="mb-2">
            <label className="block text-xs font-semibold text-slate-700 mb-1 [.theme-liquid-glass_&]:text-white/80">
              {t('Palette')}
            </label>
            <div className="grid grid-cols-8 gap-2">
              {paletteColors.map((color) => (
                <button
                  key={color}
                  type="button"
                  title={color}
                  onClick={() => handleColorSelect(color)}
                  className="w-full aspect-square rounded border-2 border-black/50 hover:opacity-80 [.theme-liquid-glass_&]:border-white/20"
                  style={{ backgroundColor: color }}
                  disabled={disabled}
                  aria-label={color}
                />
              ))}
            </div>
          </div>
        )}

        <div className="flex items-center justify-between mt-3">
          <div className="text-xs text-slate-500 [.theme-liquid-glass_&]:text-white/60">
            {t('Click outside or press ESC to close')}
          </div>
          {allowClear && (
            <button
              type="button"
              onClick={handleClear}
              className={clsx(
                'text-xs font-semibold px-2 py-1 rounded-md border-2 border-black',
                'bg-slate-100 hover:-translate-x-px hover:-translate-y-px hover:shadow-[2px_2px_0_0_#000]',
                '[.theme-liquid-glass_&]:border-white/20 [.theme-liquid-glass_&]:bg-white/10 [.theme-liquid-glass_&]:text-white',
                '[.theme-liquid-glass_&]:hover:translate-x-0 [.theme-liquid-glass_&]:hover:translate-y-0 [.theme-liquid-glass_&]:hover:shadow-none'
              )}
              disabled={disabled}
            >
              {t('Clear')}
            </button>
          )}
        </div>
      </motion.div>
    </motion.div>
  );

  const PopoverContent = (
    <AnimatePresence>
      {isOpen && PopoverBody}
    </AnimatePresence>
  );

  return (
    <div className={clsx('w-full relative', containerClassName)}>
      {resolvedLabel && (
        <label
          htmlFor={fieldId + '-swatch'}
          className={clsx(
            'block text-sm font-medium text-gray-700 mb-1',
            '[.theme-liquid-glass_&]:text-white/90',
            labelClassName
          )}
        >
          {resolvedLabel}
        </label>
      )}

      <button
        type="button"
        id={fieldId + '-swatch'}
        ref={referenceElement}
        onClick={handleSwatchClick}
        className={swatchBaseClasses}
        style={{ backgroundColor: value || '#ffffff' }}
        disabled={disabled}
        aria-expanded={isOpen}
        aria-haspopup="dialog"
        aria-label={resolvedLabel ? `Choose ${resolvedLabel}` : 'Choose color'}
      >
        <input type="hidden" id={fieldId} name={fieldName} value={value} />
      </button>

      {resolvedErrorText && (
        <p className="mt-1.5 text-xs text-red-600 [.theme-liquid-glass_&]:text-rose-400">
          {resolvedErrorText}
        </p>
      )}
      {resolvedDescriptionText && !hasError && (
        <p className="mt-1.5 text-xs text-gray-500 [.theme-liquid-glass_&]:text-white/60">
          {resolvedDescriptionText}
        </p>
      )}

      {portal
        ? typeof document !== 'undefined' && ReactDOM.createPortal(PopoverContent, document.body)
        : PopoverContent}
    </div>
  );
};

/* Helpers */

function isValidHex(hex: string) {
  return /^#([0-9A-Fa-f]{6}|[0-9A-Fa-f]{3})$/i.test(hex);
}

function rgbToHex(rgb: string): string | null {
  const match =
    rgb.match(/rgba?\(\s*(\d+)\s*,\s*(\d+)\s*,\s*(\d+)/i) ||
    [];
  const r = clamp255(match[1]);
  const g = clamp255(match[2]);
  const b = clamp255(match[3]);
  if (r == null || g == null || b == null) return null;
  return '#' + [r, g, b].map(toHex2).join('');
}

function clamp255(s?: string) {
  if (!s) return null;
  const n = Math.max(0, Math.min(255, parseInt(s, 10)));
  return isNaN(n) ? null : n;
}

function toHex2(n: number) {
  const h = n.toString(16);
  return h.length === 1 ? '0' + h : h;
}

function guessHex(color: string): string {
  if (!color || typeof color !== 'string') return '#000000';
  if (isValidHex(color)) return normalizeHex(color);
  const hex = rgbToHex(color);
  if (hex) return hex;
  return '#000000';
}

function normalizeHex(hex: string) {
  if (!hex.startsWith('#')) return '#' + hex;
  return hex.length === 4
    ? '#' + hex[1] + hex[1] + hex[2] + hex[2] + hex[3] + hex[3]
    : hex;
}
